import { useState, useEffect } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import { type ITenant } from "@/hooks/useTenants";

export interface ITenantDetail extends ITenant {
  activeUserEmails: string[];
}

export const useTenant = (tenantId: string) => {
  const { authUser } = useAuth();
  const [tenant, setTenant] = useState<ITenantDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setTenant(null);
    setError(null);

    if (!authUser) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const tenantRef = doc(db, "tenants", tenantId);
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    getDoc(tenantRef)
      .then(() => {
        if (!isMounted) return;

        unsubscribe = onSnapshot(
          tenantRef,
          (docSnap) => {
            if (!docSnap.exists()) {
              setTenant(null);
              setError(null);
              setIsLoading(false);
              return;
            }

            const data = docSnap.data();
            const activeUserEmails: string[] = data.active_user_emails ?? [];

            setTenant({
              tenantId: docSnap.id,
              name: data.name,
              status: data.status,
              adminUids: data.admin_uids ?? [],
              memberCount: activeUserEmails.length,
              activeUserEmails,
            });
            setError(null);
            setIsLoading(false);
          },
          (err) => {
            logError(err);
            setError(err);
            setIsLoading(false);
          },
        );
      })
      .catch((err: Error) => {
        if (!isMounted) return;
        logError(err);
        setError(err);
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [tenantId, authUser]);

  return { tenant, isLoading, error };
};
